import type { CatalogProperty } from 'src/api/generated';
import { findExecutionPropertyCode } from './execution-property-code.util';
import { formatPropertyCode } from 'src/modules/shared/utils/catalog/property-code.util';
import type {
  ExecutionActionSummary,
  ExecutionBlockerSummary,
  ExecutionContactAddressSummary,
  ExecutionContextSummary,
  ExecutionDetailPresentation,
  ExecutionMessageSummary,
  ExecutionPlanSummary,
  ExecutionPropertySummary,
  ExecutionTriggerSummary,
} from 'src/modules/automations/types/executions/execution-detail-presentation.type';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readString = (
  record: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = record?.[key];
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
};

const readNumber = (
  record: Record<string, unknown> | undefined,
  key: string
): number | undefined => {
  const value = record?.[key];
  return typeof value === 'number' ? value : undefined;
};

const readBoolean = (
  record: Record<string, unknown> | undefined,
  key: string
): boolean | undefined => {
  const value = record?.[key];
  return typeof value === 'boolean' ? value : undefined;
};

const readDisplayValue = (
  record: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = record?.[key];
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  return typeof value === 'number' ? String(value) : undefined;
};

const getOutputs = (step: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(step) || !isRecord(step.outputs)) {
    return undefined;
  }

  return step.outputs;
};

const getPhase = (step: unknown): string | undefined =>
  isRecord(step) ? readString(step, 'phase')?.toLowerCase() : undefined;

const getStatus = (step: unknown): string =>
  isRecord(step) ? readString(step, 'status')?.toLowerCase() ?? '' : '';

const getCreatedAt = (step: unknown): string | undefined =>
  isRecord(step) ? readString(step, 'createdAt') : undefined;

const getLatestPhaseOutputs = (
  steps: readonly unknown[],
  phase: string
): Record<string, unknown> | undefined => {
  const matchingSteps = steps
    .filter((step) => getPhase(step) === phase)
    .sort(
      (left, right) =>
        new Date(getCreatedAt(right) ?? 0).getTime() -
        new Date(getCreatedAt(left) ?? 0).getTime()
    );

  return matchingSteps.length > 0 ? getOutputs(matchingSteps[0]) : undefined;
};

const getContactName = (
  outputs: Record<string, unknown>
): string | undefined => {
  if (!Array.isArray(outputs.conversationContacts)) {
    return undefined;
  }

  const contact = outputs.conversationContacts.find(isRecord);
  if (!contact) {
    return undefined;
  }

  const fullName = [
    readString(contact, 'firstName'),
    readString(contact, 'lastName'),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName.length > 0 ? fullName : readString(contact, 'name');
};

const getInteractionContext = (
  outputs: Record<string, unknown>
): {
  messages: ExecutionMessageSummary[];
  totalMessages: number;
  callsCount: number;
} => {
  const interactions = isRecord(outputs.interactions)
    ? outputs.interactions
    : undefined;
  const sourceMessages = Array.isArray(interactions?.messages)
    ? interactions.messages.filter(isRecord)
    : [];
  const messages = sourceMessages
    .slice(-3)
    .flatMap((message): ExecutionMessageSummary[] => {
      const content = readString(message, 'content');
      if (!content) return [];
      const direction = readString(message, 'direction');
      return [{ content, ...(direction ? { direction } : {}) }];
    });

  return {
    messages,
    totalMessages: sourceMessages.length,
    callsCount: Array.isArray(interactions?.calls)
      ? interactions.calls.length
      : 0,
  };
};

const getPropertyLabel = (
  outputs: Record<string, unknown>
): string | undefined => {
  const property = isRecord(outputs.propertyOfInterest)
    ? outputs.propertyOfInterest
    : undefined;
  const propertyId =
    readNumber(property, 'id') ?? readNumber(outputs, 'propertyOfInterestId');

  return (
    readString(property, 'name') ??
    readString(property, 'addressDisplay') ??
    (typeof propertyId === 'number' ? String(propertyId) : undefined)
  );
};

const getContactAddresses = (
  outputs: Record<string, unknown>
): ExecutionContactAddressSummary[] => {
  if (!Array.isArray(outputs.conversationContacts)) return [];
  const contact = outputs.conversationContacts.find(isRecord);
  if (!contact || !Array.isArray(contact.addresses)) return [];

  return contact.addresses.filter(isRecord).flatMap((address) => {
    const value = readString(address, 'address');
    if (!value) return [];
    const channelType = readString(address, 'channelType');
    return [{ address: value, ...(channelType ? { channelType } : {}) }];
  });
};

const getTriggerContext = (
  outputs: Record<string, unknown>
): ExecutionTriggerSummary | undefined => {
  if (!Array.isArray(outputs.triggers)) return undefined;
  const trigger = outputs.triggers.find(isRecord);
  if (!trigger) return undefined;

  const data = isRecord(trigger.data) ? trigger.data : undefined;
  const nestedData = isRecord(data?.data) ? data.data : undefined;
  const sender = isRecord(nestedData?.sender) ? nestedData.sender : undefined;
  const triggerId = readString(trigger, 'triggerId');
  const resourceAction = readString(data, 'resourceAction');
  const resourceType = readString(data, 'resourceType');
  const objective =
    readString(data, 'objective') ??
    readString(data, 'title') ??
    readString(nestedData, 'objective') ??
    readString(nestedData, 'title');
  const content = readString(nestedData, 'content');
  const senderName = readString(sender, 'displayName');

  return {
    ...(triggerId ? { triggerId } : {}),
    ...(resourceAction ? { resourceAction } : {}),
    ...(resourceType ? { resourceType } : {}),
    ...(objective ? { objective } : {}),
    ...(content ? { content } : {}),
    ...(senderName ? { sender: senderName } : {}),
  };
};

const getPriceLabel = (
  property: Record<string, unknown>,
  amountKey: string,
  currencyKey: string
): string | undefined => {
  const amount = readDisplayValue(property, amountKey);
  if (!amount) return undefined;
  const currency = readString(property, currencyKey);
  return currency ? `${amount} ${currency}` : amount;
};

const getPropertyLocationDetails = (
  property: Record<string, unknown>
): Partial<ExecutionPropertySummary> => {
  const streetAddress = [
    readString(property, 'streetName'),
    readString(property, 'streetNumber'),
    readString(property, 'unit'),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ');
  const streetAddressValue =
    streetAddress.length > 0 ? streetAddress : undefined;
  const address = readString(property, 'addressDisplay') ?? streetAddressValue;
  const locality = readString(property, 'locality');
  const region = readString(property, 'region');
  const bedrooms = readNumber(property, 'bedrooms');
  const bathrooms = readNumber(property, 'bathrooms');

  return {
    ...(address ? { address } : {}),
    ...(locality ? { locality } : {}),
    ...(region ? { region } : {}),
    ...(bedrooms !== undefined ? { bedrooms } : {}),
    ...(bathrooms !== undefined ? { bathrooms } : {}),
  };
};

const getPropertyCommercialDetails = (
  property: Record<string, unknown>
): Partial<ExecutionPropertySummary> => {
  const rentPrice = getPriceLabel(
    property,
    'rentPriceAmount',
    'rentPriceCurrency'
  );
  const salePrice = getPriceLabel(
    property,
    'salePriceAmount',
    'salePriceCurrency'
  );
  const sourceSystem = readString(property, 'sourceSystem');
  const sourceId = readString(property, 'sourceId');
  const sourceUrl = readString(property, 'sourceUrl');

  return {
    ...(rentPrice ? { rentPrice } : {}),
    ...(salePrice ? { salePrice } : {}),
    ...(sourceSystem ? { sourceSystem } : {}),
    ...(sourceId ? { sourceId } : {}),
    ...(sourceUrl ? { sourceUrl } : {}),
  };
};

const getPropertyDetails = (
  outputs: Record<string, unknown>
): ExecutionPropertySummary | undefined => {
  const property = isRecord(outputs.propertyOfInterest)
    ? outputs.propertyOfInterest
    : undefined;
  if (!property) return undefined;

  const id =
    readNumber(property, 'id') ?? readNumber(outputs, 'propertyOfInterestId');
  const name = readString(property, 'name');
  const operation = readString(property, 'operation');
  const propertyType = readString(property, 'propertyType');
  const isActive = readBoolean(property, 'isActive');

  return {
    ...(id !== undefined ? { id } : {}),
    ...(name ? { name } : {}),
    ...(operation ? { operation } : {}),
    ...(propertyType ? { propertyType } : {}),
    ...(isActive !== undefined ? { isActive } : {}),
    ...getPropertyLocationDetails(property),
    ...getPropertyCommercialDetails(property),
  };
};

const buildContext = (
  steps: readonly unknown[],
  properties: ReadonlyMap<number, CatalogProperty> | undefined
): ExecutionContextSummary => {
  const outputs = getLatestPhaseOutputs(steps, 'fetch-context');
  if (!outputs) {
    return {
      contactAddresses: [],
      recentMessages: [],
      totalMessages: 0,
      callsCount: 0,
    };
  }

  const funnel = isRecord(outputs.funnel) ? outputs.funnel : undefined;
  const funnelStatus = isRecord(outputs.currentFunnelStatus)
    ? outputs.currentFunnelStatus
    : undefined;
  const interactionContext = getInteractionContext(outputs);
  const latestInteraction =
    interactionContext.messages.at(-1)?.content ?? undefined;
  const trigger = getTriggerContext(outputs);
  const originalProperty = getPropertyDetails(outputs);
  const propertyId =
    originalProperty?.id ?? readNumber(outputs, 'propertyOfInterestId');
  const displayCode = findExecutionPropertyCode(properties, propertyId);
  const propertyDetails = originalProperty
    ? { ...originalProperty, displayCode }
    : undefined;
  const legacyPropertyLabel = getPropertyLabel(outputs);
  const propertyLabel = displayCode
    ? [
        legacyPropertyLabel === String(propertyId)
          ? undefined
          : legacyPropertyLabel,
        formatPropertyCode({ displayCode, fallback: String(propertyId) }),
      ]
        .filter(Boolean)
        .join(' · ')
    : legacyPropertyLabel;

  return {
    ...(getContactName(outputs) ? { contact: getContactName(outputs) } : {}),
    ...(readString(funnel, 'name')
      ? { funnel: readString(funnel, 'name') }
      : {}),
    ...(readString(funnelStatus, 'name')
      ? { funnelStatus: readString(funnelStatus, 'name') }
      : {}),
    ...(readString(funnelStatus, 'description')
      ? { funnelStatusDescription: readString(funnelStatus, 'description') }
      : {}),
    ...(latestInteraction ? { latestInteraction } : {}),
    ...(propertyLabel ? { property: propertyLabel } : {}),
    ...(trigger ? { trigger } : {}),
    contactAddresses: getContactAddresses(outputs),
    recentMessages: interactionContext.messages,
    totalMessages: interactionContext.totalMessages,
    callsCount: interactionContext.callsCount,
    ...(propertyDetails ? { propertyDetails } : {}),
  };
};

const readTodo = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }

  if (!isRecord(value)) {
    return undefined;
  }

  return (
    readString(value, 'content') ??
    readString(value, 'objective') ??
    readString(value, 'description')
  );
};

const buildPlan = (steps: readonly unknown[]): ExecutionPlanSummary => {
  const outputs = getLatestPhaseOutputs(steps, 'planner');
  if (!outputs) {
    return { todos: [] };
  }

  const todos = Array.isArray(outputs.todos)
    ? outputs.todos
        .map(readTodo)
        .filter((todo): todo is string => Boolean(todo))
    : [];

  return {
    ...(readString(outputs, 'objective')
      ? { objective: readString(outputs, 'objective') }
      : {}),
    todos,
  };
};

const buildActions = (steps: readonly unknown[]): ExecutionActionSummary[] =>
  steps.flatMap((step, index) => {
    if (!isRecord(step)) {
      return [];
    }

    const toolName = readString(step, 'toolName');
    if (
      !toolName ||
      toolName.includes('publish-employee-phase-tools-completed')
    ) {
      return [];
    }

    const executionId = readString(step, 'executionId');
    const createdAt = readString(step, 'createdAt');

    return [
      {
        key: executionId ? `${executionId}-${index}` : `${toolName}-${index}`,
        toolName,
        status: readString(step, 'status') ?? '',
        ...(createdAt ? { createdAt } : {}),
      },
    ];
  });

const buildBlocker = (
  steps: readonly unknown[]
): ExecutionBlockerSummary | undefined => {
  const outputs = getLatestPhaseOutputs(steps, 'validator');
  if (!outputs) {
    return undefined;
  }

  const action = readString(outputs, 'action');
  if (action !== 'blocked-todo' && action !== 'blocked-objective') {
    return undefined;
  }

  const blocker = isRecord(outputs.blocker) ? outputs.blocker : undefined;
  const reason = readString(outputs, 'reason') ?? readString(blocker, 'reason');
  if (!reason) {
    return undefined;
  }

  const affectedWork = readString(outputs, 'affectedTodo');
  return {
    reason,
    ...(affectedWork ? { affectedWork } : {}),
  };
};

const getOutcome = (
  steps: readonly unknown[],
  blocker: ExecutionBlockerSummary | undefined
): ExecutionDetailPresentation['outcome'] => {
  if (blocker) {
    return 'blocked';
  }

  const latestValidatorOutputs = getLatestPhaseOutputs(steps, 'validator');
  const validatorAction = latestValidatorOutputs
    ? readString(latestValidatorOutputs, 'action')
    : undefined;
  const remainingTodosCount = latestValidatorOutputs
    ? readNumber(latestValidatorOutputs, 'remainingTodosCount')
    : undefined;
  if (
    validatorAction === 'completed-objective' ||
    validatorAction === 'cancelled-objective' ||
    (remainingTodosCount === 0 &&
      (validatorAction === 'completed-todo' ||
        validatorAction === 'cancelled-todo'))
  ) {
    return 'completed';
  }

  const latestStep = steps.at(-1);
  if (
    latestStep !== undefined &&
    ['failed', 'error'].includes(getStatus(latestStep))
  ) {
    return 'failed';
  }

  return 'running';
};

/**
 * Derives the business-facing execution summary from the existing step contract.
 *
 * @param steps - Raw execution steps returned by the API.
 * @param properties - Separately fetched catalog properties for current display codes.
 * @returns Summary projection used by the execution detail page.
 */
export const buildExecutionDetailPresentation = (
  steps: readonly unknown[],
  properties?: ReadonlyMap<number, CatalogProperty>
): ExecutionDetailPresentation => {
  const blocker = buildBlocker(steps);

  return {
    outcome: getOutcome(steps, blocker),
    context: buildContext(steps, properties),
    plan: buildPlan(steps),
    actions: buildActions(steps),
    ...(blocker ? { blocker } : {}),
  };
};
